import React from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Pencil, Trash2, Coins, Camera, Zap, Share2, Copy, Check, Loader2 } from "lucide-react";
import { OP_TYPE_MAP } from "../lib/opTypes";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const TYPE_META = Object.fromEntries(
  Object.entries(OP_TYPE_MAP).map(([k, t]) => [k, { label: t.short, color: t.color, Icon: t.Icon }])
);

function formatBRL(n) {
  const sign = n < 0 ? "-" : "";
  return `${sign}R$ ${Math.abs(n).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(iso) {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y.slice(2)}`;
}

export default function EntriesTable({ entries, onEdit, onDelete, onToggleFinalized, onEntryChanged, showFinalized = false }) {
  const [confirmId, setConfirmId] = React.useState(null);
  const [settingHit, setSettingHit] = React.useState(null); // entry id being updated
  const [ticketUrl, setTicketUrl] = React.useState(null); // preview modal
  const [ticketBlobUrl, setTicketBlobUrl] = React.useState(null);
  const [shareState, setShareState] = React.useState(null); // {entry, code}
  const [sharingId, setSharingId] = React.useState(null);
  const [copied, setCopied] = React.useState(false);

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Código copiado");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Não foi possível copiar — selecione e copie manualmente");
    }
  };

  const shareEntry = async (e) => {
    setSharingId(e.id);
    try {
      const res = await api.post(`/entries/${e.id}/share`);
      setCopied(false);
      setShareState({ entry: e, code: res.data.code });
      copyText(res.data.code);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao gerar código");
    } finally {
      setSharingId(null);
    }
  };

  React.useEffect(() => {
    let objUrl = null;
    let cancelled = false;
    if (!ticketUrl) return;
    (async () => {
      try {
        const relative = ticketUrl.startsWith("/api") ? ticketUrl.slice(4) : ticketUrl;
        const res = await api.get(relative, { responseType: "blob" });
        if (cancelled) return;
        objUrl = URL.createObjectURL(res.data);
        setTicketBlobUrl(objUrl);
      } catch {
        toast.error("Erro ao carregar imagem");
      }
    })();
    return () => {
      cancelled = true;
      if (objUrl) URL.revokeObjectURL(objUrl);
      setTicketBlobUrl(null);
    };
  }, [ticketUrl]);

  const setHitSide = async (id, side) => {
    setSettingHit(id);
    try {
      const res = await api.patch(`/entries/${id}/hit-side`, { hit_side: side });
      toast.success(side === "principal" ? "Marcado: bilhete bateu na principal" : "Marcado: defesas bateram");
      if (onEntryChanged) onEntryChanged(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao atualizar");
    } finally {
      setSettingHit(null);
    }
  };

  if (!entries || entries.length === 0) {
    return (
      <div
        data-testid="entries-empty"
        className="flex flex-col items-center justify-center rounded-xl border border-dashed border-white/10 bg-[#121212] p-16 text-center"
      >
        <div className="mb-3 text-4xl">📊</div>
        <h3 className="font-display text-lg font-bold text-white">Nenhuma entrada ainda</h3>
        <p className="mt-1 max-w-xs text-sm text-zinc-500">
          Clique em <span className="text-[#D4FF00]">&quot;Nova operação&quot;</span> para registrar sua primeira aposta.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="entries-table">
      {/* MOBILE CARD LIST (< md) */}
      <div className="grid grid-cols-1 gap-3 md:hidden">
        {entries.map((e) => {
          const meta = TYPE_META[e.operation_type] || TYPE_META.surebet;
          const Icon = meta.Icon;
          const totalStake = e.bookmakers.reduce((a, b) => a + b.stake, 0);
          const isFreebetType = e.operation_type === "freebet" || e.operation_type === "extracao_freebet";
          const isProcFB = e.operation_type === "proc_freebet";
          const isProcLucro = isProcFB && e.freebet_sub_mode === "profit_guaranteed";
          const effectiveStake = isProcFB
            ? totalStake
            : isFreebetType && e.freebet_value
            ? Math.max(0, totalStake - e.freebet_value)
            : totalStake;
          const isPos = (e.profit || 0) >= 0;
          const time = e.created_at
            ? new Date(e.created_at).toLocaleTimeString("pt-BR", {
                hour: "2-digit",
                minute: "2-digit",
                timeZone: "America/Sao_Paulo",
              })
            : "";
          const [ey, em, ed] = (e.date || "").split("-");
          const dateFmt = ey ? `${ed}/${em}/${ey.slice(2)}` : "";
          const nPrincipals = Math.max(1, Math.min(e.principal_count || 1, 5));
          return (
            <div
              key={e.id}
              data-testid={`entry-card-${e.id}`}
              className="rounded-xl border border-white/10 bg-[#121212] p-4"
            >
              {/* Row 1: date/time + bookmaker chips + finalize checkbox */}
              <div className="mb-3 flex items-start gap-3">
                <div
                  data-testid={`entry-type-icon-${e.id}`}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
                  style={{ backgroundColor: `${meta.color}26`, border: `1px solid ${meta.color}66` }}
                  title={meta.label}
                >
                  <Icon style={{ color: meta.color, width: 18, height: 18 }} strokeWidth={2.2} />
                </div>
                <div className="min-w-[68px] shrink-0">
                  <div className="font-mono-num text-sm font-bold text-white">{dateFmt}</div>
                  {time && <div className="mt-0.5 font-mono-num text-[0.7rem] text-zinc-500">{time}</div>}
                  <div className="mt-2">
                    <span
                      className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[0.7rem] font-semibold"
                      style={{
                        borderColor: `${meta.color}55`,
                        backgroundColor: `${meta.color}15`,
                        color: meta.color,
                      }}
                    >
                      <Icon className="h-2.5 w-2.5" />
                      {meta.label}
                    </span>
                  </div>
                </div>
                <div className="min-w-0 flex-1 space-y-1">
                  {e.bookmakers.map((b, i) => {
                    const isPrincipal = i < nPrincipals;
                    const chipColor = isPrincipal ? "#D4FF00" : "#71717A";
                    return (
                      <div
                        key={i}
                        className="flex items-center justify-between gap-2 rounded-md border px-2 py-1"
                        style={{
                          borderColor: `${chipColor}40`,
                          backgroundColor: isPrincipal ? `${chipColor}15` : "rgba(0,0,0,0.3)",
                        }}
                      >
                        <span
                          className="truncate text-[0.75rem] font-semibold"
                          style={{ color: isPrincipal ? chipColor : "#D4D4D8" }}
                        >
                          {b.name || "—"}
                        </span>
                        <span className="shrink-0 font-mono-num text-[0.75rem] text-zinc-300">
                          R$ {b.stake.toFixed(2)}
                        </span>
                      </div>
                    );
                  })}
                  {(e.home_team && e.away_team) && (
                    <div className="mt-1 truncate text-xs font-medium text-white">
                      {e.home_team} <span className="text-zinc-500">×</span> {e.away_team}
                    </div>
                  )}
                  {e.freebet_value != null && e.freebet_value > 0 && (
                    <div
                      data-testid={`freebet-chip-mobile-${e.id}`}
                      className="mt-1 inline-flex items-center gap-1 rounded-full border border-[#D4FF00]/40 bg-[#D4FF00]/10 px-2 py-0.5 text-[0.7rem] font-bold uppercase tracking-widest text-[#D4FF00]"
                    >
                      <Coins className="h-3 w-3" />
                      R$ {e.freebet_value.toFixed(0)}
                    </div>
                  )}
                  <div className="mt-1 flex flex-wrap gap-1">
                    {e.early_payout && (
                      <span
                        data-testid={`pa-badge-mobile-${e.id}`}
                        className="inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-400/10 px-2 py-0.5 text-[0.62rem] font-semibold uppercase tracking-widest text-amber-400"
                        title={`Pagamento antecipado${e.pa_type ? ` — ${e.pa_type}` : ""}`}
                      >
                        <Zap className="h-2.5 w-2.5" />
                        PA{e.pa_type ? ` · ${e.pa_type}` : ""}
                      </span>
                    )}
                    {e.weekly_365 && (
                      <span
                        data-testid={`weekly-365-badge-mobile-${e.id}`}
                        className="inline-flex items-center gap-1 rounded-full border border-blue-400/40 bg-blue-400/10 px-2 py-0.5 text-[0.62rem] font-semibold uppercase tracking-widest text-blue-400"
                      >
                        Semanal 365
                      </span>
                    )}
                  </div>
                </div>
                {showFinalized && (
                  <div className="shrink-0">
                    <Checkbox
                      data-testid={`finalize-mobile-${e.id}`}
                      checked={!!e.finalized}
                      onCheckedChange={(v) => onToggleFinalized && onToggleFinalized(e.id, !!v)}
                      className="border-zinc-600 data-[state=checked]:bg-[#D4FF00] data-[state=checked]:text-black"
                    />
                  </div>
                )}
              </div>

              {/* Row 2: investido/receita/lucro */}
              <div className="mb-3 grid grid-cols-3 gap-2 rounded-lg border border-white/5 bg-black/30 p-3">
                <div>
                  <div className="text-[0.62rem] uppercase tracking-widest text-zinc-500">Investido</div>
                  <div className="mt-0.5 font-mono-num text-sm font-bold text-white">
                    R$ {effectiveStake.toFixed(2)}
                  </div>
                </div>
                <div>
                  <div className="text-[0.62rem] uppercase tracking-widest text-zinc-500">Receita</div>
                  <div className="mt-0.5 font-mono-num text-sm font-bold text-white">
                    R$ {(e.revenue || 0).toFixed(2)}
                  </div>
                </div>
                <div>
                  <div className="text-[0.62rem] uppercase tracking-widest text-zinc-500">Lucro</div>
                  <div
                    className="mt-0.5 font-mono-num text-sm font-bold"
                    style={{ color: isPos ? "#00FF66" : "#FF3B30" }}
                  >
                    {isPos ? "+" : "-"}R$ {Math.abs(e.profit || 0).toFixed(2)}
                  </div>
                </div>
              </div>

              {e.notes && (
                <div
                  data-testid={`entry-notes-mobile-${e.id}`}
                  className="mb-3 rounded-md border border-white/5 bg-black/20 px-3 py-2"
                >
                  <div className="mb-0.5 text-[0.62rem] uppercase tracking-widest text-zinc-500">
                    Observações
                  </div>
                  <div className="text-xs italic leading-snug text-zinc-300">{e.notes}</div>
                </div>
              )}
              {/* Hit-side widget for proc_freebet + profit_guaranteed */}
              {isProcLucro && (
                <div className="mb-3 flex items-center gap-1.5">
                  <span className="text-[0.62rem] uppercase tracking-widest text-zinc-500">Resultado:</span>
                  {[
                    { v: "principal", label: "Principal", color: "#D4FF00" },
                    { v: "defense", label: "Defesa", color: "#22D3EE" },
                  ].map((o) => {
                    const active = e.hit_side === o.v;
                    return (
                      <button
                        key={o.v}
                        type="button"
                        disabled={settingHit === e.id}
                        onClick={() => setHitSide(e.id, active ? null : o.v)}
                        className="rounded-full border px-2 py-0.5 text-[0.7rem] font-semibold uppercase tracking-widest disabled:opacity-50"
                        style={{
                          borderColor: active ? o.color : "rgba(255,255,255,0.15)",
                          backgroundColor: active ? `${o.color}22` : "transparent",
                          color: active ? o.color : "#A1A1AA",
                        }}
                      >
                        {o.label}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Row 3: actions */}
              <div className="flex items-center justify-end gap-1">
                {e.ticket_image_url && (
                  <Button
                    data-testid={`ticket-icon-mobile-${e.id}`}
                    variant="ghost"
                    size="icon"
                    onClick={() => setTicketUrl(e.ticket_image_url)}
                    className="h-9 w-9 text-[#D4FF00] hover:bg-[#D4FF00]/10"
                  >
                    <Camera className="h-4 w-4" />
                  </Button>
                )}
                <Button
                  data-testid={`share-mobile-${e.id}`}
                  variant="ghost"
                  size="icon"
                  onClick={() => shareEntry(e)}
                  disabled={sharingId === e.id}
                  title="Gerar código para compartilhar"
                  className="h-9 w-9 text-zinc-400 hover:bg-[#D4FF00]/10 hover:text-[#D4FF00]"
                >
                  {sharingId === e.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
                </Button>
                <Button
                  data-testid={`edit-mobile-${e.id}`}
                  variant="ghost"
                  size="icon"
                  onClick={() => onEdit(e)}
                  className="h-9 w-9 text-zinc-400 hover:bg-white/5 hover:text-white"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  data-testid={`delete-mobile-${e.id}`}
                  variant="ghost"
                  size="icon"
                  onClick={() => setConfirmId(e.id)}
                  className="h-9 w-9 text-zinc-400 hover:bg-red-500/10 hover:text-red-400"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* DESKTOP TABLE (md+) */}
      <div
        className="hidden overflow-hidden rounded-xl border border-white/10 bg-[#121212] md:block"
      >
        <div className="overflow-x-auto">
          <table className="w-full">
          <thead className="border-b border-white/10 bg-black/40">
            <tr className="text-left">
              <th className="w-14 py-3 pl-4" />
              <th className="px-5 py-3 text-[0.7rem] font-medium uppercase tracking-widest text-zinc-500">Data</th>
              <th className="px-5 py-3 text-[0.7rem] font-medium uppercase tracking-widest text-zinc-500">Tipo</th>
              <th className="px-5 py-3 text-[0.7rem] font-medium uppercase tracking-widest text-zinc-500">Casas de apostas</th>
              <th className="px-5 py-3 text-right text-[0.7rem] font-medium uppercase tracking-widest text-zinc-500">Investido</th>
              <th className="px-5 py-3 text-right text-[0.7rem] font-medium uppercase tracking-widest text-zinc-500">Receita</th>
              <th className="px-5 py-3 text-right text-[0.7rem] font-medium uppercase tracking-widest text-zinc-500">Lucro</th>
              {showFinalized && (
                <th className="px-3 py-3 text-center text-[0.7rem] font-medium uppercase tracking-widest text-zinc-500">
                  Finalizada
                </th>
              )}
              <th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody>
            {entries.map((e, idx) => {
              const meta = TYPE_META[e.operation_type] || TYPE_META.surebet;
              const Icon = meta.Icon;
              const totalStake = e.bookmakers.reduce((a, b) => a + b.stake, 0);
              const isFreebetType = e.operation_type === "freebet" || e.operation_type === "extracao_freebet";
              const isProcFB = e.operation_type === "proc_freebet";
              const isProcLucro = isProcFB && e.freebet_sub_mode === "profit_guaranteed";
              const nPrincipals = Math.max(1, Math.min(e.principal_count || 1, 5));
              const effectiveStake = isProcFB
                ? totalStake
                : isFreebetType && e.freebet_value
                ? Math.max(0, totalStake - e.freebet_value)
                : totalStake;
              return (
                <tr
                  key={e.id}
                  data-testid={`entry-row-${e.id}`}
                  className="border-b border-white/5 hover:bg-white/[0.02]"
                  style={{ transition: "background-color 0.15s ease" }}
                >
                  <td className="py-4 pl-4">
                    <div
                      data-testid={`entry-type-icon-${e.id}`}
                      className="flex h-10 w-10 items-center justify-center rounded-lg"
                      style={{ backgroundColor: `${meta.color}26`, border: `1px solid ${meta.color}66` }}
                      title={meta.label}
                    >
                      <Icon style={{ color: meta.color, width: 20, height: 20 }} strokeWidth={2.2} />
                    </div>
                  </td>
                  <td className="px-5 py-4 font-mono-num text-sm text-zinc-300">
                    <div>{formatDate(e.date)}</div>
                    {e.created_at && (
                      <div
                        data-testid={`entry-time-${e.id}`}
                        className="mt-0.5 text-[0.7rem] font-normal text-zinc-600"
                      >
                        {new Date(e.created_at).toLocaleTimeString("pt-BR", {
                          hour: "2-digit",
                          minute: "2-digit",
                          timeZone: "America/Sao_Paulo",
                        })}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <span
                      className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[0.75rem] font-medium"
                      style={{
                        borderColor: `${meta.color}40`,
                        backgroundColor: `${meta.color}12`,
                        color: meta.color,
                      }}
                    >
                      <Icon className="h-3 w-3" />
                      {meta.label}
                      {e.operation_type === "aumentada" && e.boost_percent != null && (
                        <span className="text-zinc-400">· {e.boost_percent}%</span>
                      )}
                      {isProcFB && e.freebet_sub_mode && (
                        <span className="text-zinc-400">
                          · {e.freebet_sub_mode === "guaranteed" ? "FB Garantida" : "Lucro Garantido"}
                        </span>
                      )}
                    </span>
                    {(e.operation_type === "freebet" || e.operation_type === "extracao_freebet" || isProcFB) && e.freebet_value != null && e.freebet_value > 0 && (
                      <span
                        data-testid={`freebet-chip-${e.id}`}
                        className="ml-1.5 inline-flex items-center gap-1 rounded-full border border-[#D4FF00]/40 bg-[#D4FF00]/10 px-2 py-0.5 text-[0.75rem] font-bold uppercase tracking-widest text-[#D4FF00]"
                        title={`Freebet de R$ ${e.freebet_value.toFixed(2)}`}
                      >
                        <Coins className="h-3.5 w-3.5" strokeWidth={2.5} />
                        R$ {e.freebet_value.toFixed(0)}
                      </span>
                    )}
                    {e.early_payout && (
                      <span
                        data-testid={`early-payout-badge-${e.id}`}
                        className="ml-1.5 inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-400/10 px-2 py-0.5 text-[0.7rem] font-semibold uppercase tracking-widest text-amber-400"
                        title={`Pagamento antecipado${e.pa_type ? ` — ${e.pa_type}` : ""}`}
                      >
                        PA{e.pa_type ? ` · ${e.pa_type}` : ""}
                      </span>
                    )}
                    {e.weekly_365 && (
                      <span
                        data-testid={`weekly-365-badge-${e.id}`}
                        className="ml-1.5 inline-flex items-center gap-1 rounded-full border border-blue-400/40 bg-blue-400/10 px-2 py-0.5 text-[0.7rem] font-semibold uppercase tracking-widest text-blue-400"
                      >
                        Semanal 365
                      </span>
                    )}
                    {e.ticket_image_url && (
                      <button
                        type="button"
                        data-testid={`ticket-icon-${e.id}`}
                        onClick={() => setTicketUrl(e.ticket_image_url)}
                        title="Ver bilhete"
                        aria-label="Ver bilhete"
                        className="ml-2 inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#D4FF00]/40 bg-[#D4FF00]/10 text-[#D4FF00] transition hover:border-[#D4FF00] hover:bg-[#D4FF00]/25 hover:scale-105"
                      >
                        <Camera className="h-4 w-4" strokeWidth={2} />
                      </button>
                    )}
                    {isProcLucro && (
                      <div
                        data-testid={`hit-side-widget-${e.id}`}
                        className="mt-2 flex flex-wrap items-center gap-1.5 rounded-md border border-white/10 bg-black/30 px-2 py-1.5"
                      >
                        <span className="text-[0.62rem] uppercase tracking-widest text-zinc-500">
                          Resultado:
                        </span>
                        {[
                          { v: "principal", label: "Principal", color: "#D4FF00" },
                          { v: "defense", label: "Defesa", color: "#22D3EE" },
                        ].map((o) => {
                          const active = e.hit_side === o.v;
                          return (
                            <button
                              key={o.v}
                              type="button"
                              data-testid={`hit-side-${o.v}-${e.id}`}
                              disabled={settingHit === e.id}
                              onClick={() => setHitSide(e.id, active ? null : o.v)}
                              className="rounded-full border px-2 py-0.5 text-[0.7rem] font-semibold uppercase tracking-widest disabled:opacity-50"
                              style={{
                                borderColor: active ? o.color : "rgba(255,255,255,0.15)",
                                backgroundColor: active ? `${o.color}22` : "transparent",
                                color: active ? o.color : "#A1A1AA",
                                transition: "all 0.15s ease",
                              }}
                              title={
                                o.v === "principal"
                                  ? "Bilhete bateu na casa principal — receita normal"
                                  : "Defesa bateu — receita de defesa + freebet re-emitida"
                              }
                            >
                              {o.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex flex-wrap gap-1.5">
                      {e.bookmakers.map((b, i) => {
                        const nPrincipals = Math.max(1, Math.min(e.principal_count || 1, 5));
                        const isPrincipal = i < nPrincipals;
                        const isFreebetMain = isPrincipal && (e.operation_type === "extracao_freebet" || e.operation_type === "proc_freebet");
                        return (
                          <span
                            key={i}
                            className="inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs"
                            style={
                              isPrincipal
                                ? { borderColor: "#D4FF0055", backgroundColor: "#D4FF0010", color: "#D4FF00" }
                                : { borderColor: "rgba(255,255,255,0.1)", backgroundColor: "rgba(0,0,0,0.4)", color: "#D4D4D8" }
                            }
                            title={isPrincipal ? `Principal ${i + 1}` : `Defesa ${i - nPrincipals + 1}`}
                          >
                            <span className="text-[0.62rem] uppercase tracking-widest opacity-60">
                              {isPrincipal ? `P${nPrincipals > 1 ? i + 1 : ""}` : `D${i - nPrincipals + 1}`}
                            </span>
                            {b.name}
                            {isFreebetMain ? (
                              <span className="ml-1 font-mono-num text-[0.75rem] opacity-70">FB</span>
                            ) : (
                              <span className="ml-1 font-mono-num text-[0.75rem] opacity-70">
                                R$ {b.stake.toFixed(2)}
                              </span>
                            )}
                          </span>
                        );
                      })}
                    </div>
                    {(e.home_team || e.away_team) ? (
                      <div className="mt-1.5 flex items-center gap-1.5 text-xs">
                        <span className="rounded border border-white/10 bg-black/40 px-1.5 py-0.5 font-semibold text-zinc-200">
                          {e.home_team || "?"}
                        </span>
                        <span className="text-zinc-500">×</span>
                        <span className="rounded border border-white/10 bg-black/40 px-1.5 py-0.5 font-semibold text-zinc-200">
                          {e.away_team || "?"}
                        </span>
                      </div>
                    ) : e.match ? (
                      <div className="mt-1.5 text-xs font-semibold text-zinc-300">{e.match}</div>
                    ) : null}
                    {e.notes && (
                      <div
                        data-testid={`entry-notes-${e.id}`}
                        className="mt-2 inline-block max-w-full rounded-md border border-white/5 bg-black/30 px-2.5 py-1 text-xs italic text-zinc-300"
                      >
                        <span className="mr-1 text-[0.62rem] font-semibold uppercase not-italic tracking-widest text-zinc-500">Obs:</span>
                        {e.notes}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-4 text-right font-mono-num text-sm text-zinc-300">
                    R$ {effectiveStake.toFixed(2)}
                    {isFreebetType && e.freebet_value ? (
                      <div className="text-[0.7rem] text-zinc-500">bruto {totalStake.toFixed(2)}</div>
                    ) : null}
                  </td>
                  <td className="px-5 py-4 text-right font-mono-num text-sm text-zinc-300">
                    R$ {e.revenue.toFixed(2)}
                  </td>
                  <td
                    className="px-5 py-4 text-right font-mono-num text-sm font-bold"
                    style={{ color: e.profit >= 0 ? "#00FF66" : "#FF3B30" }}
                  >
                    {e.profit >= 0 ? "+" : "-"}R$ {Math.abs(e.profit).toFixed(2)}
                  </td>
                  {showFinalized && (
                    <td className="px-3 py-4 text-center">
                      <Checkbox
                        data-testid={`finalize-toggle-${e.id}`}
                        checked={!!e.finalized}
                        onCheckedChange={(v) => onToggleFinalized && onToggleFinalized(e.id, !!v)}
                        className="border-white/30 data-[state=checked]:border-[#00FF66] data-[state=checked]:bg-[#00FF66] data-[state=checked]:text-black"
                        aria-label="Marcar partida como finalizada"
                      />
                    </td>
                  )}
                  <td className="px-5 py-4 text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        data-testid={`share-entry-${e.id}`}
                        variant="ghost"
                        size="icon"
                        onClick={() => shareEntry(e)}
                        disabled={sharingId === e.id}
                        title="Gerar código para compartilhar"
                        className="h-8 w-8 text-zinc-500 hover:bg-[#D4FF00]/10 hover:text-[#D4FF00]"
                      >
                        {sharingId === e.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Share2 className="h-3.5 w-3.5" />}
                      </Button>
                      <Button
                        data-testid={`edit-entry-${e.id}`}
                        variant="ghost"
                        size="icon"
                        onClick={() => onEdit(e)}
                        className="h-8 w-8 text-zinc-500 hover:bg-white/5 hover:text-white"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        data-testid={`delete-entry-${e.id}`}
                        variant="ghost"
                        size="icon"
                        onClick={() => setConfirmId(e.id)}
                        className="h-8 w-8 text-zinc-500 hover:bg-red-500/10 hover:text-red-400"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent className="border-white/10 bg-[#121212]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Excluir entrada?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Esta ação não pode ser desfeita. A operação será removida permanentemente do histórico.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn" className="border-white/10 bg-transparent text-zinc-300 hover:bg-white/5 hover:text-white">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-btn"
              onClick={() => {
                onDelete(confirmId);
                setConfirmId(null);
              }}
              className="bg-red-500 text-white hover:bg-red-600"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!shareState} onOpenChange={(o) => !o && setShareState(null)}>
        <DialogContent data-testid="share-code-dialog" className="border-white/10 bg-[#121212] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-lg text-white">
              <Share2 className="h-4 w-4 text-[#D4FF00]" />
              Código da operação
            </DialogTitle>
          </DialogHeader>
          {shareState && (
            <div className="space-y-4">
              <p className="text-sm text-zinc-400">
                Envie este código. Quem receber cola em <span className="font-semibold text-white">Nova operação → Importar</span> e
                todos os dados (casas, confronto, valores, data) já vêm preenchidos.
              </p>
              <button
                type="button"
                data-testid="share-code-value"
                onClick={() => copyText(shareState.code)}
                className="flex w-full items-center justify-between gap-3 rounded-xl border border-[#D4FF00]/40 bg-[#D4FF00]/10 px-4 py-3 text-left hover:bg-[#D4FF00]/15"
              >
                <span className="font-mono-num text-3xl font-black tracking-[0.25em] text-[#D4FF00]">{shareState.code}</span>
                {copied ? <Check className="h-5 w-5 text-[#22C55E]" /> : <Copy className="h-5 w-5 text-zinc-300" />}
              </button>
              <div className="rounded-lg border border-white/5 bg-black/30 px-3 py-2 text-xs text-zinc-400">
                {formatDate(shareState.entry.date)} · {(TYPE_META[shareState.entry.operation_type] || TYPE_META.surebet).label}
                {shareState.entry.home_team && shareState.entry.away_team && ` · ${shareState.entry.home_team} × ${shareState.entry.away_team}`}
                {" · "}
                {shareState.entry.bookmakers.map((b) => `${b.name} R$ ${b.stake.toFixed(2)}`).join(" / ")}
                {" · Receita "}{formatBRL(shareState.entry.revenue || 0)}
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  data-testid="share-code-copy-btn"
                  onClick={() => copyText(shareState.code)}
                  className="gap-2 bg-[#D4FF00] font-bold text-black hover:bg-[#c4ee00]"
                >
                  <Copy className="h-4 w-4" /> Copiar código
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!ticketUrl} onOpenChange={(o) => !o && setTicketUrl(null)}>
        <DialogContent
          data-testid="ticket-view-modal"
          className="max-w-3xl border-white/10 bg-[#121212] p-4"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-lg text-white">Bilhete da aposta</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center bg-black/40">
            {ticketBlobUrl ? (
              <img
                src={ticketBlobUrl}
                alt="Bilhete"
                data-testid="ticket-view-img"
                className="max-h-[70vh] w-auto object-contain"
              />
            ) : (
              <div className="flex h-64 items-center justify-center text-sm text-zinc-500">
                Carregando…
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
